/**
 * 金额转中文
 * 思路：
 *                              个
 *      十     百      千       万
 *      十万   百万    千万     亿
 *      十亿   百亿    千亿
 *
 *                              1
 *      2      3       4        5
 *      6      7       8        9
 *      10
 *
 * 计算步骤
 * 1. 获取当前数值大小
 * 2. 排除个位后 数值按个，十，百，千有规律的重复 所以计算其和4的余数 pos % 4
 * 3. pos = 0 ~ 3 没有最大单位
 *    pos = 4 ~ 7 最大单位是万
 *    pos = 8 ~ 11 最大单位是亿
 * pos / 4 的整数就是最大单位
 *
 */
export function getAmountChinese(val) {
  const amount = +val;
  if (Number.isNaN(amount) || amount < 0) return "";
  const NUMBER = ["零", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];
  const N_UNIT1 = ["", "拾", "佰", "仟"];
  const N_UNIT2 = ["", "万", "亿"];
  const D_UNIT = ["角", "分", "厘", "毫"];
  let [integer, decimal] = amount.toString().split(".");
  if (integer && integer.length > 12) return "金额过大无法计算";
  let res = "";
  // 整数部分
  if (integer) {
    for (let i = 0, len = integer.length; i < len; i++) {
      const num = integer.charAt(i);
      const pos = len - i - 1; // 排除个位后 所处的索引位置
      if (num === "0") {
        // 当前位 等于 0 且下一位也等于 0 则可跳过计算
        if (i === len - 1) {
          if (integer.length === 1) res += "零"; // 0.35 这种情况不可跳过计算
          break;
        }
        if (integer.charAt(i + 1) === "0") continue;
      }
      res += NUMBER[num];
      if (parseInt(num)) res += N_UNIT1[pos % 4];
      if (pos % 4 === 0) res += N_UNIT2[Math.floor(pos / 4)];
    }
  }
  res += "圆";
  // 小数部分
  if (parseInt(decimal)) {
    for (let i = 0; i < 4; i++) {
      const num = decimal.charAt(i);
      if (parseInt(num)) res += NUMBER[num] + D_UNIT[i];
    }
  } else {
    res += "整";
  }
  return res;
}

/**
 * 计算两个时间差经历的时间的文字描述
 * @param {*} timestamp - 毫秒
 */
export const timeCalculate = (start, end) => {
  const label = ["分钟", "小时", "天", "月", "年"];
  const unit = [60, 60, 24, 30, 12];
  let restTime = Math.floor((end - start) / 1000);
  let res = "";
  for (let i = 0, len = unit.length; i < len; i++) {
    const pos = len - i; // 从年开始算，分钟换算成年 === Math.pow(60, 4)
    const temp = unit.slice(0, pos).reduce((p, c) => p * c, 1);
    const time = Math.floor(restTime / temp);
    time > 0 && (res += time + label[pos - 1]);
    restTime -= time * temp;
  }
  return res;
};

/**
 * 简易防抖函数
 * @param {Function} func -防抖目标函数
 * @param {Number}} gap - 防抖时间间隔
 */
export const debounce = (func, gap) => {
  let timer;
  return function() {
    timer && clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, arguments);
    }, gap);
  };
};
/**
 * 下划线转驼峰
 * @param {String} name - 字符串
 */
export const toHump = name =>
  name.replace(/\_(\w)/g, function(all, letter) {
    return letter.toUpperCase();
  });
/**
 * 将用户输入的连续单个数字合并为一个数
 * @param {Array} expressions - 记录计算表达式的数组
 * @returns {Array} 新的数组
 */
export const mergeNumberOfExps = expressions => {
  const res = [];
  const isNumChar = n => /^[\d|\.]$/.test(n);
  for (let i = 0; i < expressions.length; i++) {
    if (i > 0 && isNumChar(expressions[i - 1]) && isNumChar(expressions[i])) {
      res[res.length - 1] += expressions[i];
      continue;
    }
    res.push(expressions[i]);
  }
  return res;
};
/**
 * 校验表达式是否符合计算法则
 * @param {Array} expressions - 合并数字后的表达式数组
 * @returns {Boolean}
 */
export const validExp = (expressions, mergeNum = true) => {
  const temp = mergeNum ? mergeNumberOfExps(expressions) : expressions;
  const arr = temp.filter(t => !"()".includes(t));
  // 去括号后 length应该为奇数  并且第一个字符和最后一个字符应该为数字而非计算符号
  if (
    temp.length % 2 === 0 ||
    arr.length % 2 === 0 ||
    Number.isNaN(+arr[0]) ||
    Number.isNaN(+arr[arr.length - 1])
  ) {
    return false;
  }
  for (let i = 0; i < arr.length - 1; i += 2) {
    if (typeof +arr[i] !== "number" || !Number.isNaN(+arr[i + 1])) return false;
  }
  return true;
};

/**
 * 中缀转后缀（逆波兰 Reverse Polish Notation）
 * @param {Array} exps - 中缀表达式数组
 */
export const toRPN = exps => {
  const s1 = []; // 符号栈
  const s2 = []; // 输出栈
  const getTopVal = stack =>
    stack.length > 0 ? stack[stack.length - 1] : null;
  const levelCompare = (c1, c2) => {
    const getIndex = c => ["+-", "×÷", "()"].findIndex(t => t.includes(c));
    return getIndex(c1) - getIndex(c2);
  };
  exps.forEach(t => {
    if (typeof t === "string" && Number.isNaN(Number(t))) {
      // 是符号
      if (t === "(") {
        s1.push(t);
      } else if (t === ")") {
        let popVal;
        do {
          popVal = s1.pop();
          popVal !== "(" && s2.push(popVal);
        } while (s1.length && popVal !== "(");
      } else {
        let topVal = getTopVal(s1);
        if (!topVal) {
          // s1 为空 直接push
          s1.push(t);
        } else {
          while (topVal && topVal !== "(" && levelCompare(topVal, t) >= 0) {
            // 优先级 >= t 弹出到s2
            s2.push(s1.pop());
            topVal = getTopVal(s1);
          }
          s1.push(t);
        }
      }
      return;
    }
    s2.push(t); // 数字直接入栈
  });
  while (s1.length) {
    s2.push(s1.pop());
  }
  return s2;
};
/**
 * 计算后缀表达式的值
 * @param {Array} rpnExps - 后缀表达式
 */
export const calcRPN = rpnExps => {
  rpnExps = rpnExps.concat();
  const calc = (x, y, type) => {
    let a1 = Number(x),
      a2 = Number(y);
    switch (type) {
      case "+":
        return a1 + a2;
      case "-":
        return a1 - a2;
      case "×":
        return a1 * a2;
      case "÷":
        return a1 / a2;
    }
  };
  for (let i = 2; i < rpnExps.length; i++) {
    if ("+-×÷".includes(rpnExps[i])) {
      let val = calc(rpnExps[i - 2], rpnExps[i - 1], rpnExps[i]);
      rpnExps.splice(i - 2, 3, val);
      i = i - 2;
    }
  }
  return rpnExps[0];
};

// uuid 生成工具
export const getUuid = (len = 20, radix) => {
  let chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split(
    ""
  );
  let uuid = [];
  radix = radix || chars.length;
  if (len) {
    for (let i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)];
  } else {
    let r;
    uuid[8] = uuid[13] = uuid[18] = uuid[23] = "-";
    uuid[14] = "4";
    for (let i = 0; i < 36; i++) {
      if (!uuid[i]) {
        r = 0 | (Math.random() * 16);
        uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r];
      }
    }
  }

  return uuid.join("");
};

// 处理审批数据
export const initProcessData = (item, showStart = true) => {
  let processData = [];
  const formatData = obj => {
    if (!obj.properties) {
      return;
    }
    let approvers =
      obj.type === "approver"
        ? obj.properties.approvers
        : obj.properties.menbers;
    let type = obj.properties.assigneeType === "role" ? 2 : 1;
    let ids = _.map(approvers, "targetId").join(",");
    let title = obj.properties.title;
    let params = {
      title,
      content: obj.content,
      nodeType: obj.type === "approver" ? 1 : 2,
      nodeName: title,
      nodeContent: obj.content,
      auditMode: obj.properties.auditMode || "3",
      auditStyleId: obj.properties.auditStyleId || "",
      auditStyleName: obj.properties.auditStyleName || "",
      downAddFlag: obj.properties.downAddFlag,
      flag: obj.properties.flag || "1",
      auditStyleType: obj.properties.auditStyleType || 1,
      requireAuditOpinion:
        obj.properties.formStyle && obj.properties.formStyle.remarkChecked
          ? 1
          : 0,
      requireAttachment:
        obj.properties.formStyle && obj.properties.formStyle.fileIdsChecked
          ? 1
          : 0,
      type,
      roleIds: type === 2 ? ids : "",
      userIds: type === 1 ? ids : ""
    };
    if ((showStart && obj.type === "start") || obj.type !== "start") {
      processData.push(params);
    }
    if (obj.childNode) {
      formatData(obj.childNode);
    }
  };
  formatData(item);
  return processData;
};
